/**
 * Shared API Utilities
 * Common helper functions used across all API hooks
 */

import { getBrowserSupabaseClient } from "../../lib/supabase-browser";
import { OAUTH_CONFIG } from "./config";

export { isCalendarNotConnectedError } from "../../lib/calendar-not-connected";

// ============================================================================
// QUERY PARAMETER UTILITIES
// ============================================================================

/**
 * Build query parameters object from individual parameters
 * Filters out undefined/null values to keep URLs clean
 */
export function buildQueryParams(params: Record<string, any>): Record<string, string> {
  const queryParams: Record<string, string> = {};

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams[key] = typeof value === "string" ? value : String(value);
    }
  });

  return queryParams;
}

// ============================================================================
// ERROR HANDLING UTILITIES
// ============================================================================

/**
 * Extract error message from API response
 */
export function getErrorMessage(error: any, defaultMessage: string): string {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    defaultMessage
  );
}

/**
 * Format error response for logging
 */
export function formatErrorResponse(error: any): Record<string, any> {
  return {
    message: error?.message,
    status: error?.response?.status,
    statusText: error?.response?.statusText,
    data: error?.response?.data,
    code: error?.response?.data?.code,
  };
}

// ============================================================================
// AUTHENTICATION UTILITIES
// ============================================================================

/**
 * Get authentication token with retry logic
 * Used primarily for OAuth flows where token might not be immediately available
 */
export async function getAuthTokenWithRetry(
  getAuthToken: () => string | null,
  maxAttempts: number = OAUTH_CONFIG.maxAttempts,
  retryDelay: number = OAUTH_CONFIG.retryDelay
): Promise<string> {
  const supabase = getBrowserSupabaseClient();
  let token: string | null = null;

  // Try to get session directly from Supabase first
  try {
    const { data: { session } } = await supabase.auth.getSession();
    token = session?.access_token || null;
  } catch (error) {
    console.error("Error getting Supabase session:", error);
  }

  // Fallback to auth context if Supabase session not available
  if (!token) {
    token = getAuthToken();
  }

  // Wait for auth to be ready (with timeout)
  // After OAuth redirect, session might not be immediately available
  let attempts = 0;

  while (!token && attempts < maxAttempts) {
    await new Promise((resolve) => setTimeout(resolve, retryDelay));

    // Try Supabase again
    try {
      const { data: { session } } = await supabase.auth.getSession();
      token = session?.access_token || null;
    } catch (error) {
      // Ignore errors, continue retrying
    }

    // Also try auth context
    if (!token) {
      token = getAuthToken();
    }

    attempts++;
  }

  if (!token) {
    throw new Error(
      "No authentication token. Please ensure you are logged in and try again. If the problem persists, please refresh the page and try again."
    );
  }

  return token;
}

// ============================================================================
// DEBUG LOGGING UTILITIES
// ============================================================================

/**
 * Log debug information in development mode only
 */
export function logDebug(label: string, data: any): void {
  if (process.env.NODE_ENV === "development") {
    console.log(`📊 [${label}]`, data);
  }
}

/**
 * Log warning in development mode only
 */
export function logWarning(label: string, message: string, data?: any): void {
  if (process.env.NODE_ENV === "development") {
    console.warn(`⚠️ [${label}] ${message}`, data);
  }
}

/**
 * Log error
 */
export function logError(label: string, message: string, data?: any): void {
  console.error(`❌ [${label}] ${message}`, data);
}

// ============================================================================
// EVENT COUNTING UTILITIES
// ============================================================================

/**
 * Count events by source/calendar
 * Useful for debug logging to understand data distribution
 */
export function countEventsBySource(
  events: Array<{ source?: string }>
): Record<string, number> {
  return events.reduce(
    (acc, event) => {
      const source = event.source || "unknown";
      acc[source] = (acc[source] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );
}

/**
 * Validate events have required fields
 */
export function validateCalendarEvents(
  events: Array<{ id?: string; title?: string; start?: string; end?: string }>
): { valid: number; invalid: number } {
  const valid = events.filter((e) => e.id && e.title && e.start && e.end).length;
  const invalid = events.length - valid;
  return { valid, invalid };
}
